<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import MediaflowService from '@/services/MediaflowService'
import StreamStatsService from '@/services/StreamStatsService'
import { useNotification } from "@kyvg/vue3-notification"
import FormButton from '../Atoms/FormButton.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import IconBase from '../icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue'
import IconArrowRight from '../icon/IconArrowRight.vue'
import IconLinkChain from '../icon/IconLinkChain.vue'
import IconElipsisVertical from '../icon/IconElipsisVertical.vue'
import Badge from '../Badge.vue'
import StreamThumbnail from '../ui/StreamThumbnail.vue'
import StreamThumb from '../ui/StreamThumb.vue'
import StreamCardBadges from '../ui/StreamCard/StreamCardBadges.vue'

const { notify } = useNotification()

const store = useStore()
const router = useRouter()

const instance = getCurrentInstance();
const root = instance.proxy.$root;

const scopeAlive = ref(true)
const mediaPulse = ref(null)
const mediaPulseTimeoutCtrl = ref(null)

const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const props = defineProps({
	mediaflow: {
		type: Object,
		default() { return {}; }
	},
	viewMode: {
		type: String,
		default: 'grid',
	},
})

const subscribeMediaPulse = async () => {
	(async function loop() {
		if (!scopeAlive.value) return;

		let timeoutInterval = 5000;
		let shouldUpdatePulse = props.mediaflow.enabled;
		if (document.hidden) {
			shouldUpdatePulse = false;
			timeoutInterval = 6000;
		}

		if (shouldUpdatePulse) {
			if (!document.hasFocus()) {
				timeoutInterval = 6000;
			}
		}
		if (shouldUpdatePulse) {
			try {
				const streamKey = props.mediaflow?.key;
				const pulseObject = await StreamStatsService.getStreamMediaPulse(
					streamKey
				);
				onMediaPulse(pulseObject);
			} catch (e) {
				console.log(e);
			}
		}
		mediaPulseTimeoutCtrl.value = setTimeout(loop, timeoutInterval);
	})();
}

const onMediaPulse = (pulseObject = {}) => {
	if (mediaPulse.value?.withoutPushStats) {
		const oldPushStats = mediaPulse.value.push_stats;
		pulseObject.push_stats = oldPushStats;
	}
	mediaPulse.value = pulseObject;
}

const unsubscribeMediaPulse = () => {
	clearTimeout(mediaPulseTimeoutCtrl.value);
}
onMounted(async () => {
	subscribeMediaPulse()
})
onUnmounted(() => {
	unsubscribeMediaPulse()
	scopeAlive.value = false
})

const onDeleteMediaflow = async () => {
	hideModal('delete-mediaflow-' + props.mediaflow._id);
	try {
		await MediaflowService.deleteMediaflow(props.mediaflow._id);
		store.dispatch('Mediaflows/removeMediaflow', { id: props.mediaflow._id });
		notify({ group: 'success', text: 'Cloud Production Deleted' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Cloud Production delete failed' })
	}
}

const showDeleteModal = (ev) => {
	ev?.preventDefault?.();
	ev?.stopPropagation?.();
	showModal('delete-mediaflow-' + props.mediaflow._id);
}

const destinationStream = computed(() => {
	if (!props.mediaflow.destination) return null;
	return allInOneStreams.value.find(stream => stream._id === props.mediaflow.destination);
})

const destinationLabel = computed(() => {
	if (!destinationStream.value) return 'No Destination Selected'
	return destinationStream.value.name.length > 40
		? destinationStream.value.name.slice(0, 40)
		: destinationStream.value.name
})

const flowsLabel = computed(() => {
	const count = props.mediaflow.flows?.length ?? 0
	return `${count} ${count === 1 ? 'Flow' : 'Flows'}`
})

const toggleMediaflow = async (ev) => {
	ev?.preventDefault?.();
	ev?.stopPropagation?.();
	try {
		await MediaflowService.toggleMediaflow(props.mediaflow._id);
		store.dispatch('Mediaflows/toggleMediaflow', { id: props.mediaflow._id });
		notify({ group: "success", text: `Cloud Production ${props.mediaflow.enabled ? 'enabled' : 'disabled'}` });
	} catch (err) {
		notify({ group: "error", text: err.message || "Cloud Production source update failed" });
	}
};

const open = () => {
	router.push({
		name: 'MediaflowContent',
		params: { id: props.mediaflow._id },
		query: { sourceTab: 'livestream' },
	})
}

</script>

<template>
	<!-- LIST VIEW -->
	<div v-if="shouldShowCompactModeStreams"
		class="flex flex-wrap md:flex-nowrap gap-x-3 gap-y-1 items-center w-full relative rounded-lg group bg-surface-2 transition-colors duration-200 hover:bg-surface-3 px-3 py-2 cursor-pointer"
		@click="open">
		<div class="relative shrink-0 overflow-hidden rounded h-12 w-24 bg-gradient-to-b from-surface-darker to-black">
			<StreamThumb class="mediaflow-thumb w-full h-full" :stream="mediaflow" :media-pulse="mediaPulse" :compact-mode="true" />
		</div>

		<div class="flex min-w-0 flex-1 basis-0 md:basis-auto items-center gap-2">
			<div class="flex flex-col min-w-0 flex-1">
				<div class="flex items-center gap-2 min-w-0">
					<h3 class="truncate text-sm md:text-base text-light-1 font-medium m-0 min-w-0" :title="mediaflow.name">
						{{ mediaflow.name }}
					</h3>
					<Badge class="shrink-0">{{ flowsLabel }}</Badge>
				</div>
				<p class="text-tiny truncate flex items-center gap-1.5 m-0"
					:class="mediaflow.destination ? 'text-surface-8' : 'text-surface-6'">
					<icon-base class="shrink-0" :width="12" :height="12" width-view-box="14" height-view-box="14" fill="none">
						<icon-link-chain />
					</icon-base>
					<span class="truncate">{{ destinationLabel }}</span>
				</p>
			</div>
			<div class="shrink-0 md:hidden" @click.stop>
				<DropDownMenu isIcon position="right" type="secondary" no-border>
					<template #toggle-button>
						<icon-base class="!ml-1">
							<IconElipsisVertical />
						</icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem :disabled="mediaflow.enabled && !!mediaPulse?.alive" @click="toggleMediaflow">
							{{ mediaflow.enabled ? 'Disable' : 'Enable' }}
						</DropDownMenuItem>
						<DropDownMenuItem :disabled="!!mediaPulse?.alive" @click="showDeleteModal">
							Delete
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</div>
		</div>

		<StreamCardBadges
			:stream="mediaflow"
			:media-pulse="mediaPulse"
			:compact-mode="true"
			:hide-preview="false"
		/>

		<div class="hidden md:flex items-center gap-2 shrink-0" @click.stop>
			<FormButton type="danger" size="sm" isIcon aria-label="Delete" :disabled="mediaPulse?.alive"
				@click="showDeleteModal">
				<icon-base>
					<icon-bin />
				</icon-base>
			</FormButton>
			<FormButton v-if="mediaflow.enabled" type="secondary" size="sm" :disabled="mediaPulse?.alive"
				@click="toggleMediaflow">
				Disable
			</FormButton>
			<FormButton v-else type="main" size="sm" @click="toggleMediaflow">
				Enable
			</FormButton>
		</div>

		<confirm-modal
			:modalId="'delete-mediaflow-' + mediaflow._id"
			message="Are you sure you want to delete this cloud production? This action cannot be undone."
			modal-type="danger"
			@modal-confirm="onDeleteMediaflow"
		/>
	</div>

	<!-- GRID VIEW -->
	<div v-else class="flex relative group flex-col bg-surface-2 rounded-lg h-full">
		<router-link class="text-reset hover:no-underline flex flex-col h-full"
			:to="{ name: 'MediaflowContent', params: { id: mediaflow._id }, query: { sourceTab: 'livestream' } }">
			<div class="relative overflow-hidden rounded-t-lg">
				<StreamThumbnail
					:stream="mediaflow"
					:media-pulse="mediaPulse"
					:obs="true"
					:compact-mode="false"
				/>
				<Badge :type="mediaPulse?.alive ? 'online' : 'offline'" :title="mediaPulse?.alive ? 'Live' : 'Offline'"
					class="inline-flex absolute top-2 left-3 z-10" />
				<div
					class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
				>
					<span class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-surface-3/90 rounded-full px-3.5 py-1.5 shadow-lg">
						Manage
						<icon-base :width="14" :height="14" width-view-box="24" height-view-box="24" fill="none">
							<icon-arrow-right />
						</icon-base>
					</span>
				</div>
			</div>
			<div class="pt-2 px-3 flex flex-col flex-1">
				<h2 class="text-base font-medium mb-1">
					{{ mediaflow.name }}
				</h2>
				<p class="text-tiny mb-0 flex items-center" :class="mediaflow.destination ? 'text-white' : 'text-surface-7'">
					<icon-base class="mr-2" :width="14" :height="14" width-view-box="14" height-view-box="14" fill="none">
						<icon-link-chain />
					</icon-base>
					{{ destinationLabel }}
				</p>

				<div class="text-right flex items-center justify-end gap-2 pb-3 mt-auto pt-2">
					<Badge class="inline-flex mr-auto">
						{{ flowsLabel }}
					</Badge>
					<FormButton @click="showDeleteModal" type="danger" size="sm" isIcon aria-label="Delete" :disabled="mediaPulse?.alive"
						class="transition-opacity duration-200 lg:opacity-0 group-hover:opacity-100 focus:opacity-100">
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
					<FormButton v-if="mediaflow.enabled" @click.prevent="toggleMediaflow" :disabled="mediaPulse?.alive" type="secondary" size="sm">
						Disable
					</FormButton>
					<FormButton v-else @click.prevent="toggleMediaflow" type="main" size="sm">
						Enable
					</FormButton>
				</div>
			</div>
		</router-link>
		<confirm-modal
			:modalId="'delete-mediaflow-' + mediaflow._id"
			message="Are you sure you want to delete this cloud production? This action cannot be undone."
			modal-type="danger"
			@modal-confirm="onDeleteMediaflow"
		/>
	</div>
</template>

<style scoped>
.mediaflow-thumb :deep(#thumb-container) {
	aspect-ratio: auto !important;
	height: 100% !important;
}
.mediaflow-thumb :deep(#thumb-container) video,
.mediaflow-thumb :deep(#thumb-container) img {
	height: 100% !important;
	width: 100% !important;
	object-fit: cover !important;
}
</style>
